import React, {
  useEffect,
  useMemo,
  useState,
  useContext
} from 'react';
import _ from 'lodash';
import ChartToolbar from './ChartToolbar';
import { createDataFetchList } from './dataFetchList';
import { Stack, StackItem } from '@patternfly/react-core';
import NodeContext from '../../services/Context/NodeContext';
import './Chart.css';
import SignalSelector from '../SignalSelector/SignalSelector';
import { parse, getComponents, getTelemetry as getTel } from '../../utils/dtdl';
import { aggregationData, timeBucketData, TimeBucket } from '../../services/constants';
import ChartViewData, { ChartViews } from './ChartViewData';
import { useResizeDetector } from 'react-resize-detector';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useHelp from '../../utils/hooks/useHelp';
import { useAsset } from 'components/Asset/queries';
import { useModelQuery } from 'components/Admin/Model/queries';
import { NodeType } from 'utils/types';
import useMeasurementSystem from 'components/Value/useMeasurementSystem';
import { useTimeseriesQuery } from 'services/TelemetryApi/queries';

const ChartView = ({ className, initialSelection, showSelectSignals = true }) => {
  const { width, height, ref } = useResizeDetector();

  const [dataOptions, setDataOptions] = useState();
  const [chosenSignals, setChosenSignals] = useState(initialSelection);
  const [graphDataPackage, setGraphDataPackage] = useState(null);
  const [resetZoom, setResetZoom] = useState(false);
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);
  const { node } = useContext(NodeContext);
  const [disableApplyButton, setDisableApplyButton] = useState(true);
  const [currentViewChooserOption, setCurrentViewChooserOption] = useState(ChartViews.LINE_GRAPH_VIEW);

  useHelp('chartpage');

  useEffect(() => {
    setChosenSignals(initialSelection);
  }, [initialSelection]);

  const { asset } = useAsset(node?.id, node?.type === NodeType.Asset);
  const { model: rawModel } = useModelQuery(asset?.modelId);
  const model = useMemo(() => {
    if (!rawModel) {
      return null;
    }
    return parse(rawModel.modelData[0], rawModel.modelData);
  }, [rawModel]);

  const chartViewData = ChartViewData[currentViewChooserOption];
  const { convertToUserPairArray } = useMeasurementSystem({ });
  const telemetryQuery = useTimeseriesQuery([dataOptions],
    createDataFetchList(dataOptions?.signalSets),
    dataOptions?.timespan,
    dataOptions?.aggregate,
    !!dataOptions);

  const availableSchemas = useMemo(() => {
    return !_.isEmpty(dataOptions) ? _.intersection(
      chartViewData.schemas,
      (!TimeBucket.RAW) ? aggregationData[dataOptions.aggregationFunction]?.schemas : chartViewData.schemas,
      timeBucketData[dataOptions.aggregate].schemas)
      : [];
  }, [chartViewData.schemas, dataOptions]);

  const tree = useMemo(() => {
    if (!_.isEmpty(model) && !_.isEmpty(asset) && (asset?.modelId === model?.['@id'])) {
      const telemetryBlock = (idList, breadcrumbList, graphNameList, displayName, signalSchema) => {
        return {
          id: idList.join('/'),
          name: displayName,
          breadcrumb: breadcrumbList.join(' > '),
          graphName: graphNameList.join(' > '),
          isTelemetry: true,
          type: signalSchema,
          checkProps: { checked: false },
          defaultExpanded: true,
          laneNumber: 0,
          decimalPoint: 2
        };
      };
      const compTree = _.map(getComponents({ model: model, pruneRoot: true }), comp => {
        return {
          id: `${asset.id}/${comp.name}`,
          name: model.displayName,
          checkProps: { checked: false },
          defaultExpanded: true,
          isTelemetry: false,
          children: _.map(getTel({ dtdlModel: model, component: comp.name }), t => {
            return telemetryBlock(
              [asset.id, comp.name, t.name],
              [asset.displayName, t.displayName],
              [asset.displayName, comp.displayName, t.displayName],
              t.displayName,
              t.schema
            );
          })
        };
      });
      const rootTree = _.map(getTel({ dtdlModel: model, component: '.' }), t => {
        return telemetryBlock(
          [asset.id, t.name],
          [asset.displayName, t.displayName],
          [asset.displayName, t.displayName],
          t.displayName,
          t.schema
        );
      });
      const _tree = _.compact(_.concat(rootTree, compTree));

      const availableSchemas = !_.isEmpty(dataOptions) ? _.intersection(
        chartViewData.schemas,
        (!TimeBucket.RAW) ? aggregationData[dataOptions.aggregationFunction]?.schemas : chartViewData.schemas,
        timeBucketData[dataOptions.aggregate].schemas)
        : [];

      const filteredTree = _.map(_tree, item => {
        if (!item?.children) {
          return _.includes(availableSchemas, item.type) ? item : null;
        }
        const children = _.compact(_.map(item.children, child => {
          return _.includes(availableSchemas, child.type) ? child : null;
        }));
        if (!_.isEmpty(children)) {
          return { ...item, children };
        }
        return null;
      });
      return _.compact(filteredTree);

    }
    return [];
  }, [model, dataOptions, chartViewData, asset]);

  useEffect(() => {
    setDisableApplyButton(true);
    if (!telemetryQuery.data || telemetryQuery.data.length === 0) {
      setGraphDataPackage(null);
    } else {
      const newData = _.map(telemetryQuery.data, (d) => {
        // TODO: is it safe to only search for name?
        const signalInfo = _.find(model.contents, { name: d.name });
        const [internalData] = convertToUserPairArray({ values: d.data, schemaUnit: signalInfo });
        return {
          ...d,
          data: internalData
        };
      });
      setGraphDataPackage({
        assets: [_.head(dataOptions.signalSets).assetId],
        data: newData,
        signalSets: dataOptions.signalSets
      });
    }
  }, [telemetryQuery.data, convertToUserPairArray, dataOptions?.signalSets, model?.contents]);

  const CurrentView = chartViewData.component;

  return (
    <div className={classNames('chart-view-container', className)} ref={ref}>
      <Stack hasGutter>
        <StackItem>
          <ChartToolbar
            className="chart-view-toolbar"
            showSelectSignals={showSelectSignals}
            onDataOptionsChange={setDataOptions}
            onLoading={telemetryQuery.isPending}
            setResetZoom={setResetZoom}
            chosenSignals={chosenSignals}
            currentViewChooserOption={currentViewChooserOption}
            setCurrentViewChooserOption={setCurrentViewChooserOption}
            toggleDrawer={() =>
              setIsDrawerExpanded((state) => !state)
            }
          />
        </StackItem>
        <StackItem isFilled className="chart-graph-container">
          <div
            className="chart-graph-component"
          >
            <SignalSelector
              tree={tree}
              isDrawerExpanded={isDrawerExpanded}
              disableApplyButton={disableApplyButton}
              setDisableApplyButton={setDisableApplyButton}
              setChosenSignals={setChosenSignals}
              chartViewData={chartViewData}
              availableSchemas={availableSchemas}
              onCloseClick={() =>
                setIsDrawerExpanded((state) => !state)
              }
            >
              <CurrentView
                graphDataPackage={graphDataPackage}
                dataOptions={dataOptions}
                resetZoom={resetZoom}
                setResetZoom={setResetZoom}
                size={{ width, height }}
              />
            </SignalSelector>
          </div>
        </StackItem>
      </Stack>
    </div>
  );
};

ChartView.propTypes = {
  initialSelection: PropTypes.arrayOf(PropTypes.object)
};

export default React.memo(ChartView);
